import { defineMessages, MessageDescriptor } from "react-intl"
import { Journey } from "#/components/carrot-plans/types/journey"
import { JourneyStage } from "#/components/carrot-plans/types/journeyStage"

const journeyStageMessages = defineMessages({
  [JourneyStage.EXPLORING]: { defaultMessage: "Exploring, trying to learn more" },
  [JourneyStage.CHOOSING_AGENCY_ATTORNEY]: { defaultMessage: "Choosing an agency and/or attorney" },
  [JourneyStage.WORKING_WITH_AGENCY_ATTORNEY]: { defaultMessage: "Currently working with an agency and/or attorney" },
  [JourneyStage.CHOOSING_AGENCY_CLINIC_ATTORNEY]: {
    defaultMessage: "Choosing a clinic, agency/consultancy, and/or attorney"
  },
  [JourneyStage.WORKING_WITH_AGENCY_CLINIC_ATTORNEY]: {
    defaultMessage: "Currently working with a clinic, agency/consultancy, and/or attorney"
  },
  [JourneyStage.FINAL_STAGES]: { defaultMessage: "In final stages of the process" },
  [JourneyStage.GESTATIONAL_PREGNANT]: { defaultMessage: "The gestational carrier is pregnant" },
  [JourneyStage.OTHER]: { defaultMessage: "Other" },
  [JourneyStage.PRESERVATION_READY]: { defaultMessage: "Would like to receive preservation care soon" },
  [JourneyStage.PRESERVATION_STARTED]: { defaultMessage: "Currently receiving preservation care" },
  [JourneyStage.PRESERVATION_RECENTLY_RECEIVED]: { defaultMessage: "Recently received preservation care" },
  [JourneyStage.MIGHT_WANT_IUI_IVF]: { defaultMessage: "Might want IUI or IVF but curious about other options" },
  [JourneyStage.GETTING_READY_START_IUI]: { defaultMessage: "Getting ready to start IUI" },
  [JourneyStage.GETTING_READY_START_IVF]: { defaultMessage: "Getting ready to start IVF" },
  [JourneyStage.ALREADY_STARTED_IUI_CYCLE]: { defaultMessage: "Already started IUI cycle" },
  [JourneyStage.ALREADY_STARTED_IVF_CYCLE]: { defaultMessage: "Already started IVF cycle" },
  [JourneyStage.RECENTLY_COMPLETED_IUI_CYCLE]: { defaultMessage: "Recently completed IUI cycle" },
  [JourneyStage.RECENTLY_COMPLETED_IVF_CYCLE]: { defaultMessage: "Recently completed IVF cycle" },
  [JourneyStage.HAVE_NOT_STARTED]: { defaultMessage: "Haven't started yet" },
  [JourneyStage.TRY_PREGNANT_LESS_THAN_SIX_MONTHS]: { defaultMessage: "Been trying for less than 6 months" },
  [JourneyStage.TRY_PREGNANT_SIX_TO_TWELVE_MONTHS]: { defaultMessage: "Been trying for 6 to 12 months" },
  [JourneyStage.TRY_PREGNANT_OVER_ONE_YEAR]: { defaultMessage: "Been trying for over 1 year" },
  [JourneyStage.PREGNANT_FIRST]: { defaultMessage: "First trimester (fewer than 14 weeks)" },
  [JourneyStage.PREGNANT_SECOND]: { defaultMessage: "Second trimester (14-28 weeks)" },
  [JourneyStage.PREGNANT_THIRD]: { defaultMessage: "Third trimester (more than 28 weeks)" },
  [JourneyStage.POSTPARTUM]: { defaultMessage: "Postpartum" },
  [JourneyStage.NOT_SURE_PARENT]: { defaultMessage: "Not sure whether I want to be a parent" },
  [JourneyStage.LEARNING_FERTILITY]: { defaultMessage: "Want to start learning about my fertility health" },
  [JourneyStage.EXPLORING_NO_PATH]: {
    defaultMessage: "Interested in parenthood eventually but haven't chosen a path yet"
  },
  [JourneyStage.EXPLORING_MULTIPLE_PATHS]: { defaultMessage: "Started down multiple paths to parenthood" },
  [JourneyStage.EXPLORING_NEW_OPTIONS]: {
    defaultMessage: "Previous paths haven't worked out, looking for new options"
  },
  [JourneyStage.START_OF_PERI_MENOPAUSE]: { defaultMessage: "Anticipating the start of (peri)menopause" },
  [JourneyStage.MENOPAUSE_SYMPTOMS_WITH_MENSTRUAL_CYCLES]: {
    defaultMessage: "Currently experiencing menopause symptoms but still having menstrual cycles"
  },
  [JourneyStage.NO_MENSTRUAL_CYCLES]: { defaultMessage: "No longer having menstrual cycles for over one year" },
  [JourneyStage.EXPERIENCING_LOW_T_SYMPTOMS]: { defaultMessage: "Experiencing low-T-related symptoms" },
  [JourneyStage.DIAGNOSED_LOW_T]: { defaultMessage: "Diagnosed with low T" },
  [JourneyStage.HORMONE_THERAPY_INTEREST]: { defaultMessage: "Interested in hormone therapy" },
  [JourneyStage.NON_HORMONAL_SUPPORT_INTEREST]: { defaultMessage: "Interested in non-hormonal support" },
  [JourneyStage.ON_HORMONES]: { defaultMessage: "Currently on hormones" },
  [JourneyStage.GOING_OFF_HORMONES]: { defaultMessage: "Stopping/want to stop hormones" },
  [JourneyStage.OTHER_WITH_USER_INPUT]: { defaultMessage: "Other" },
  [JourneyStage.CHILD_LESS_THAN_YEAR]: { defaultMessage: "Parenting an infant (0–12 months)" },
  [JourneyStage.CHILD_ONE_TO_TWO_YEARS]: { defaultMessage: "Parenting a child (1–2 years)" },
  [JourneyStage.CHILD_THREE_TO_FIVE_YEARS]: { defaultMessage: "Parenting a child (3–5 years)" },
  [JourneyStage.CHILD_SIX_TO_EIGHT_YEARS]: { defaultMessage: "Parenting a child (6–8 years)" },
  [JourneyStage.CHILD_NINE_TO_TWELVE_YEARS]: { defaultMessage: "Parenting a child (9–12 years)" }
})

export const journeyStages = Object.keys(journeyStageMessages).map((key) => ({
  value: key as JourneyStage,
  label: journeyStageMessages[key as JourneyStage]
}))

export const journeyTypeStageOptions: Record<string, JourneyStage[]> = {
  [Journey.ADOPTION]: [
    JourneyStage.EXPLORING,
    JourneyStage.CHOOSING_AGENCY_ATTORNEY,
    JourneyStage.WORKING_WITH_AGENCY_ATTORNEY,
    JourneyStage.FINAL_STAGES
  ],
  [Journey.GESTATIONAL]: [
    JourneyStage.EXPLORING,
    JourneyStage.CHOOSING_AGENCY_CLINIC_ATTORNEY,
    JourneyStage.WORKING_WITH_AGENCY_CLINIC_ATTORNEY,
    JourneyStage.GESTATIONAL_PREGNANT,
    JourneyStage.OTHER
  ],
  [Journey.PRESERVATION]: [
    JourneyStage.EXPLORING,
    JourneyStage.PRESERVATION_READY,
    JourneyStage.PRESERVATION_STARTED,
    JourneyStage.PRESERVATION_RECENTLY_RECEIVED
  ],
  [Journey.ASSISTED_REPRODUCTION]: [
    JourneyStage.EXPLORING,
    JourneyStage.MIGHT_WANT_IUI_IVF,
    JourneyStage.GETTING_READY_START_IUI,
    JourneyStage.GETTING_READY_START_IVF,
    JourneyStage.ALREADY_STARTED_IUI_CYCLE,
    JourneyStage.ALREADY_STARTED_IVF_CYCLE,
    JourneyStage.RECENTLY_COMPLETED_IUI_CYCLE,
    JourneyStage.RECENTLY_COMPLETED_IVF_CYCLE
  ],
  [Journey.TRY_PREGNANT]: [
    JourneyStage.HAVE_NOT_STARTED,
    JourneyStage.TRY_PREGNANT_LESS_THAN_SIX_MONTHS,
    JourneyStage.TRY_PREGNANT_SIX_TO_TWELVE_MONTHS,
    JourneyStage.TRY_PREGNANT_OVER_ONE_YEAR
  ],
  [Journey.PREGNANT]: [
    JourneyStage.PREGNANT_FIRST,
    JourneyStage.PREGNANT_SECOND,
    JourneyStage.PREGNANT_THIRD,
    JourneyStage.POSTPARTUM
  ],
  [Journey.EXPLORING]: [
    JourneyStage.NOT_SURE_PARENT,
    JourneyStage.LEARNING_FERTILITY,
    JourneyStage.EXPLORING_NO_PATH,
    JourneyStage.EXPLORING_MULTIPLE_PATHS,
    JourneyStage.EXPLORING_NEW_OPTIONS
  ],
  [Journey.MENOPAUSE]: [
    JourneyStage.EXPLORING,
    JourneyStage.START_OF_PERI_MENOPAUSE,
    JourneyStage.MENOPAUSE_SYMPTOMS_WITH_MENSTRUAL_CYCLES,
    JourneyStage.NO_MENSTRUAL_CYCLES
  ],
  [Journey.LOW_TESTOSTERONE]: [
    JourneyStage.EXPLORING,
    JourneyStage.EXPERIENCING_LOW_T_SYMPTOMS,
    JourneyStage.DIAGNOSED_LOW_T
  ],
  [Journey.GENDER_AFFIRMING_CARE]: [
    JourneyStage.EXPLORING,
    JourneyStage.HORMONE_THERAPY_INTEREST,
    JourneyStage.NON_HORMONAL_SUPPORT_INTEREST,
    JourneyStage.ON_HORMONES,
    JourneyStage.GOING_OFF_HORMONES,
    JourneyStage.OTHER_WITH_USER_INPUT
  ],
  [Journey.PARENTING]: [
    JourneyStage.CHILD_LESS_THAN_YEAR,
    JourneyStage.CHILD_ONE_TO_TWO_YEARS,
    JourneyStage.CHILD_THREE_TO_FIVE_YEARS,
    JourneyStage.CHILD_SIX_TO_EIGHT_YEARS,
    JourneyStage.CHILD_NINE_TO_TWELVE_YEARS
  ]
}

export function getLabelForJourneyStage(journeyStage: string): MessageDescriptor {
  const journeyStageOption = journeyStages.find((stage) => stage.value === journeyStage)
  return journeyStageOption?.label
}

export function isPregnancyTrimesterStage(stage: string | null): boolean {
  return (
    stage === JourneyStage.PREGNANT_FIRST ||
    stage === JourneyStage.PREGNANT_SECOND ||
    stage === JourneyStage.PREGNANT_THIRD
  )
}

export function isPostpartumStage(stage: string | null): boolean {
  return stage === JourneyStage.POSTPARTUM
}

export function useOptionsForJourneyType(memberJourney: string): {
  stageOptions: { label: { defaultMessage: string }; value: string }[]
} {
  const journeyStageOptions = journeyTypeStageOptions?.[memberJourney] || []
  const stageOptions = journeyStages.filter((stage) => journeyStageOptions.includes(stage.value))

  return {
    stageOptions
  }
}
